/**
 * Notes made from a menu: New note on the canvas pane, and Paste as note on
 * the canvas pane and in the wall's add menu, with the clipboard's text.
 */
import { createDocFrame } from "@/components/layout/create-doc-frame";
import { translate } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { readClipboardText } from "@/lib/copy-to-clipboard";
import { useCanvasStore } from "@/stores/canvas-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";

export type NotePosition = { x: number; y: number };

/** A note at `position` on the canvas; without one the backend places it. */
export function createNote(position?: NotePosition, text?: string): void {
  const { currentProject, currentSpace } = useProjectStore.getState();
  if (!currentProject || !currentSpace) return;
  void createDocFrame({
    projectId: currentProject.id,
    spaceId: currentSpace.id,
    position,
    text,
    selectFrame: useCanvasStore.getState().selectFrame,
  });
}

function toast(
  id: string,
  level: "info" | "error",
  title: string,
  description: string,
): void {
  useNotificationStore.getState().addSystemToast({
    id: `${id}-${Date.now()}`,
    level,
    title,
    description,
  });
}

/** A note of the clipboard's text, at `position` on the canvas. */
export async function pasteClipboardAsNote(
  position?: NotePosition,
): Promise<void> {
  let text: string | null;
  try {
    text = await readClipboardText();
  } catch (error) {
    toast(
      "clipboard-read-fail",
      "error",
      translate(canvasMessages, "pasteFailed"),
      error instanceof Error
        ? error.message
        : translate(canvasMessages, "clipboardReadFailed"),
    );
    return;
  }
  if (text === null) {
    toast(
      "clipboard-read-unavailable",
      "error",
      translate(canvasMessages, "pasteUnavailable"),
      translate(canvasMessages, "clipboardUnreadable"),
    );
    return;
  }
  const value = text.trim();
  if (!value) {
    toast(
      "clipboard-empty",
      "info",
      translate(canvasMessages, "clipboardEmpty"),
      translate(canvasMessages, "clipboardEmptyHint"),
    );
    return;
  }
  createNote(position, value);
}
